import { INJECTION_COLUMN } from '../../../../../../../../utils/enums';
import { EditableCellV2 } from '../../../MedTableRenderer';
import AntAutoCompleteV2 from '../../../AntAutoComplete';
import AddItemChip from '../../../../../../../common/AddItemChip';
import { fetchMatchingDrugsLabTests } from '../../../../../../../../api';
import {
    getInjectionElements,
    getRxClinicId,
} from '../../../../../../../../store/selectors/prescriptionPad';
import { useDispatch, useSelector } from 'react-redux';
import { Drug, S_TYPE } from '../../../../../../../../api/fetchMatchingDrugsLabTests';
import Fuse from 'fuse.js';
import { GInjection } from '../../../../../../../../store/db';
import {
    FlatInjectionObj,
    InjectionsEntity,
} from '../../../../../../../../store/types/prescription';
import { DragHandle } from '../../../TableWithDragableRows';
import {
    setInjectionElements,
    upsertInjection,
} from '../../../../../../../../store/actions/prescriptionPad';
import { INJECTIONS_TABLE_CONTAINER } from '../InjectionTable';
import { getGArray } from '../../../../../../../../store/idbHandler';
import { getMyDbId } from '../../../../../../../../api/mactiveid';
import { DB_TABLES } from '../../../../../../../../store/dbTables';
import { useEffect, useState } from 'react';
import { AS_INLINE_EMPTY, GENIE } from '../../../../../../trackSource';
import RenameMedName from '../../../RenameMedName';

const fuseConfig = { keys: ['name', 'actual_name'], threshold: 0.2 };

const InjectionDrug = ({
    injection,
    columnKey,
    isDragMode,
}: {
    injection: FlatInjectionObj;
    columnKey: INJECTION_COLUMN;
    isDragMode: boolean;
}): JSX.Element => {
    const elements = useSelector(getInjectionElements);
    const dispatch = useDispatch();
    const [fuse, setFuse] = useState<Fuse<GInjection>>();
    const [genieElements, setGenieElements] = useState<GInjection[]>();
    const clinicId = useSelector(getRxClinicId);

    const currentElement = injection.is_added_drug
        ? elements[injection.mainIndex]?.added_drug?.[injection.currentIndex]
        : elements[injection.mainIndex];

    const isNameEdited =
        currentElement?.actual_name && currentElement.actual_name !== currentElement.name;

    useEffect(() => {
        const fetchGenieElements = async () => {
            const gElements = await getGArray<GInjection>(
                getMyDbId() || '',
                DB_TABLES['injections'],
            );
            setGenieElements(gElements);
            setFuse(new Fuse(gElements, fuseConfig));
        };
        fetchGenieElements();
    }, [injection]);

    const showAddDrugStrip =
        (injection.is_last_drug && injection.is_last_tapering) ||
        (elements[injection.mainIndex]?.added_drug &&
            elements[injection.mainIndex]?.added_drug?.length &&
            injection.is_last_drug);

    const onDrugSearch = async (value: string): Promise<{ id: string; [key: string]: any }[]> => {
        let requiredDrugs: Drug[] = [];

        if (!value) {
            const suggestions = genieElements
                ? genieElements.filter((e) => !elements.find((t) => t.id === e.id))
                : [];
            suggestions.splice(5);
            return suggestions.map((suggestion, index) => ({
                ...suggestion,
                track: {
                    label: AS_INLINE_EMPTY,
                    index: index,
                    source: GENIE,
                },
            }));
        }
        try {
            const drugResponse = await fetchMatchingDrugsLabTests(
                value,
                undefined,
                undefined,
                undefined,
                clinicId,
                undefined,
                S_TYPE.DRUG,
                true,
            );
            requiredDrugs = drugResponse.drugs || [];
        } catch (error) {
            requiredDrugs = [];
        }
        const all: { id: string; [key: string]: any }[] = [];
        if (fuse) {
            all.push(
                ...fuse.search(value).map((t) => ({
                    ...t.item,
                })),
            );
            for (let index = 0; index < requiredDrugs.length; index++) {
                if (!all.find((t) => t.id === requiredDrugs[index].id)) {
                    all.push({
                        generic_name: requiredDrugs[index].generic_name as string,
                        id: requiredDrugs[index].id,
                        df_id: requiredDrugs[index].dosage?.df_id,
                        dosage_form: requiredDrugs[index].dosage?.dosage_form,
                        name: requiredDrugs[index].name,
                    });
                }
            }
        }
        all.splice(5);
        return all;
    };

    const onResetDrugName = () => {
        const { is_added_drug, currentIndex, mainIndex } = injection;
        const clonedElement = elements[mainIndex];
        if (is_added_drug) {
            if (clonedElement.added_drug?.[currentIndex]) {
                clonedElement.added_drug[currentIndex].name = clonedElement.added_drug[currentIndex]
                    .actual_name as string;
                clonedElement.added_drug[currentIndex].actual_name = undefined;
            }
        } else {
            if (clonedElement) {
                clonedElement.name = clonedElement.actual_name as string;
                clonedElement.actual_name = undefined;
            }
        }
        dispatch(upsertInjection(clonedElement, mainIndex));
    };
    const onSaveDrugName = (name: string) => {
        const { is_added_drug, currentIndex, mainIndex } = injection;
        const clonedElement = elements[mainIndex];
        if (is_added_drug) {
            if (clonedElement.added_drug?.[currentIndex]) {
                clonedElement.added_drug[currentIndex].actual_name =
                    clonedElement.added_drug[currentIndex].name;
                clonedElement.added_drug[currentIndex].name = name;
            }
        } else {
            if (clonedElement) {
                clonedElement.actual_name = clonedElement.name;
                clonedElement.name = name;
            }
        }
        dispatch(upsertInjection(clonedElement, mainIndex));
    };

    const onDrugSelect = (option?: { [key: string]: any; id: string }) => {
        if (option) {
            const { generic_name, id, name, df_id, dosage_form } = option;
            const { is_added_drug, currentIndex, mainIndex } = injection;
            const clonedElement = elements[mainIndex];
            if (is_added_drug) {
                if (clonedElement.added_drug?.[currentIndex]) {
                    clonedElement.added_drug[currentIndex].id = id;
                    clonedElement.added_drug[currentIndex].name = name;
                    clonedElement.added_drug[currentIndex].generic_name = generic_name;
                    clonedElement.added_drug[currentIndex].df_id = df_id;
                    clonedElement.added_drug[currentIndex].dosage_form = dosage_form;
                }
            } else {
                if (clonedElement) {
                    clonedElement.id = id;
                    clonedElement.name = name;
                    clonedElement.generic_name = generic_name;
                    clonedElement.df_id = df_id;
                    clonedElement.dosage_form = dosage_form;
                }
            }
            dispatch(upsertInjection(clonedElement, mainIndex));
        }
    };
    const onAdditionalDrugClick = () => {
        const { mainIndex } = injection;
        const clonedElements = [...elements];

        if (clonedElements[mainIndex].added_drug) {
            clonedElements[mainIndex].added_drug = [
                ...(clonedElements[mainIndex].added_drug || []),
                {} as InjectionsEntity,
            ];
        } else {
            clonedElements[mainIndex].added_drug = [{} as InjectionsEntity];
        }
        dispatch(setInjectionElements(clonedElements));
    };
    return isDragMode && injection.is_added_drug ? (
        <></>
    ) : (
        <div className="hover-parent-element flex flex-row items-center">
            {injection.is_added_drug ? <div className="w-14 h-1" /> : <DragHandle />}
            <div>
                <EditableCellV2
                    fieldName={columnKey}
                    defaultValue={injection.name || ''}
                    label={'Drug name'}
                    withError={true}
                >
                    <AntAutoCompleteV2
                        autoFocus={true}
                        onSearch={onDrugSearch}
                        selectedValue={
                            injection.id && injection.name
                                ? {
                                      id: injection.id,
                                      name: injection.name,
                                  }
                                : undefined
                        }
                        debounceDelay={200}
                        getLabelToCompare={(o) => o.name}
                        placeholder={'Drug Name'}
                        optionsRenderer={DrugOptionRenderer}
                        customDropdownWidth={300}
                        onSelect={onDrugSelect}
                        key={`Injection-${columnKey}`}
                        containerIdToAttachDropdown={INJECTIONS_TABLE_CONTAINER}
                        customStyle={{ width: 150 }}
                        onChange={(value) => {
                            if (!value) {
                                const { is_added_drug, currentIndex, mainIndex } = injection;
                                const clonedElement = elements[mainIndex];
                                if (is_added_drug) {
                                    if (clonedElement.added_drug?.[currentIndex]) {
                                        clonedElement.added_drug[currentIndex].id = '';
                                        clonedElement.added_drug[currentIndex].name = '';
                                        clonedElement.added_drug[currentIndex].generic_name = '';
                                        clonedElement.added_drug[currentIndex].df_id = '';
                                        clonedElement.added_drug[currentIndex].dosage_form = '';
                                    }
                                } else {
                                    if (clonedElement) {
                                        clonedElement.id = '';
                                        clonedElement.name = '';
                                        clonedElement.generic_name = '';
                                        clonedElement.df_id = '';
                                        clonedElement.dosage_form = '';
                                    }
                                }
                                dispatch(upsertInjection(clonedElement, mainIndex));
                            }
                        }}
                    />
                </EditableCellV2>
                <div className="text-9 font-500 pl-12 text-darwin-neutral-600">
                    {injection.generic_name?.toUpperCase()}
                </div>
            </div>
            <RenameMedName
                originalName={injection.actual_name || injection.name}
                defaultName={injection.name || ''}
                onSave={onSaveDrugName}
                isNameEdited={!!isNameEdited}
                onReset={onResetDrugName}
            />
            {showAddDrugStrip ? (
                <AddItemChip title="Add Inj/IVF" onClick={onAdditionalDrugClick} />
            ) : null}
        </div>
    );
};

const DrugOptionRenderer = (option: { id: string; [key: string]: any }) => {
    return (
        <div
            className={`flex items-center justify-between mx-dropdown-item cursor-pointer font-lato w-full`}
        >
            <div className="flex items-center space-x-8">
                <div className="relative w-28 h-28 bg-darwin-accent-medicines-pink-200 rounded-full grow-0 ">
                    <p className="text-darwin-accent-medicines-pink-800 text-12 font-700 top-5 left-5 absolute">
                        INJ
                    </p>
                </div>

                <div>
                    <p className="text-neutral-1 text-14 w-full">{option.name}</p>
                    {option.generic_name && (
                        <p className="text-darwin-neutral-500 text-10 w-full">
                            {option.generic_name}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InjectionDrug;
